import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Heart, Loader2, CheckCircle, AlertCircle, RefreshCw, Trophy } from 'lucide-react';
import { API } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { TopSupporters } from './TopSupporters';

const DONATION_PRESETS = [
  { amount: 2, label: '$2' },
  { amount: 5, label: '$5' },
  { amount: 10, label: '$10' },
  { amount: 25, label: '$25' },
];

export function DonateModal({ isOpen, onClose }) {
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const [selectedAmount, setSelectedAmount] = useState(5);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');

  // Check for returning from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('donation_session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setError('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await fetch(`${API}/donations/status/${sessionId}`);
      if (!response.ok) throw new Error('Failed to check payment status');

      const data = await response.json();
      console.log('[DonateModal] Payment status:', data);

      if (data.payment_status === 'paid' || data.status === 'complete') {
        setSuccess(true);
        setLoading(false);
        toast.success('Thank you for your donation!');
        return;
      } else if (data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        setLoading(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('[DonateModal] Status check error:', err);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setError('Error checking payment status.');
        setLoading(false);
      }
    }
  };

  const handleDonate = async () => {
    const amount = isCustom ? parseFloat(customAmount) : selectedAmount;
    
    if (!amount || amount < 1) {
      toast.error('Please enter a valid amount (minimum $1)');
      return;
    }

    if (amount > 1000) {
      toast.error('Maximum donation amount is $1,000');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('[DonateModal] Creating checkout session for $' + amount);
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (isAuthenticated) {
        const authHeaders = getAuthHeaders();
        if (authHeaders?.Authorization) {
          headers.Authorization = authHeaders.Authorization;
        }
      }

      const response = await fetch(`${API}/donations/create-checkout`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          amount: amount,
          is_recurring: isRecurring,
          note: note.slice(0, 140),
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      console.log('[DonateModal] Checkout session created:', data);

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('[DonateModal] Error:', err);
      setError(err.message);
      toast.error(err.message);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setError(null);
    setLoading(false);
    onClose();
  };

  const handleSelectPreset = (amount) => {
    setSelectedAmount(amount);
    setIsCustom(false);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setCustomAmount(value);
    setIsCustom(true);
  };

  if (success) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md" data-testid="donate-success-modal">
          <div className="flex flex-col items-center py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="text-xl font-bold mb-2">Thank You!</h3>
            <p className="text-muted-foreground mb-6">
              Your donation helps keep DMV Soundstage running and supports local creators across the DMV.
            </p>
            <Button onClick={handleClose} data-testid="donate-close-btn">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="donate-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-500" />
            Support DMV Soundstage
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="donate" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="donate" data-testid="donate-tab">
              <Heart className="w-4 h-4 mr-2" />
              Donate
            </TabsTrigger>
            <TabsTrigger value="leaderboard" data-testid="leaderboard-tab">
              <Trophy className="w-4 h-4 mr-2" />
              Leaderboard
            </TabsTrigger>
          </TabsList>

          <TabsContent value="donate" className="space-y-6">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Support DMV Soundstage — your donation helps keep the platform running, supports local creators, improves features, and keeps everything ad-light. Every contribution helps build a stronger creative community.
            </p>

            {/* Amount Selection */}
            <div className="space-y-3">
              <Label>Select Amount</Label>
              <div className="grid grid-cols-4 gap-2">
                {DONATION_PRESETS.map(({ amount, label }) => (
                  <Card
                    key={amount}
                    className={`p-3 text-center cursor-pointer transition-all ${
                      !isCustom && selectedAmount === amount
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-border'
                    }`}
                    onClick={() => handleSelectPreset(amount)}
                    data-testid={`donate-preset-${amount}`}
                  >
                    <span className="font-bold">{label}</span>
                  </Card>
                ))}
              </div>
              
              {/* Custom Amount */}
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="text"
                  placeholder="Custom amount"
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  className={`pl-7 ${isCustom ? 'border-primary' : ''}`}
                  data-testid="donate-custom-amount"
              />
            </div>
          </div>

          {/* Recurring Toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
            <div>
              <p className="font-medium text-sm">Monthly Support</p>
              <p className="text-xs text-muted-foreground">Become a recurring supporter</p>
            </div>
            <Button
              variant={isRecurring ? 'default' : 'outline'}
              size="sm"
              onClick={() => setIsRecurring(!isRecurring)}
              data-testid="donate-recurring-toggle"
            >
              {isRecurring ? 'Monthly' : 'One-time'}
            </Button>
          </div>

          {/* Optional Note */}
          <div className="space-y-2">
            <Label htmlFor="note">Add a note (optional)</Label>
            <Input
              id="note"
              placeholder="Thank you for..."
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 140))}
              maxLength={140}
              data-testid="donate-note"
            />
            <p className="text-xs text-muted-foreground text-right">{note.length}/140</p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Donate Button */}
          <Button
            onClick={handleDonate}
            disabled={loading}
            className="w-full bg-gradient-to-r from-primary to-secondary hover:opacity-90"
            size="lg"
            data-testid="donate-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Heart className="w-4 h-4 mr-2" />
                Donate ${isCustom ? customAmount || '0' : selectedAmount}
                {isRecurring && '/month'}
              </>
            )}
          </Button>

          {/* Disclaimer */}
          <p className="text-xs text-muted-foreground text-center">
            Donations are voluntary and help keep the platform running. No purchase required.
          </p>
          </TabsContent>

          <TabsContent value="leaderboard">
            <TopSupporters showTitle={false} limit={10} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// Simple button to open the modal
export function DonateButton({ className = '', variant = 'outline', size = 'sm', showIcon = true }) {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setShowModal(true)}
        className={`${className}`}
        data-testid="donate-btn"
      >
        {showIcon && <Heart className="w-4 h-4 mr-1.5 text-red-500" />}
        Donate
      </Button>
      <DonateModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </>
  );
}

export default DonateModal;
